import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  Target,
  ArrowRight,
  Calendar,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Minus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkforceForecastTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/chro-enhanced/workforce-forecast`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching forecast data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { currentState, targetState, changes, briImprovement, timeline } = data;

  const segmentColors = {
    "Builder": { bg: "#1B5E20", light: "#E8F5E9", text: "#1B5E20" },
    "Accelerating": { bg: "#1565C0", light: "#E3F2FD", text: "#1565C0" },
    "Developing": { bg: "#E65100", light: "#FFF3E0", text: "#E65100" },
    "Upskilling Priority": { bg: "#7C3AED", light: "#F3E8FF", text: "#7C3AED" }
  };

  const segments = ["Builder", "Accelerating", "Developing", "Upskilling Priority"];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Workforce Capability Forecast</h2>
          <p className="text-sm text-muted-foreground">Projected workforce transformation over {timeline}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Badge className="bg-[#1a3a3a]">
            <Calendar className="w-3 h-3 mr-1" />
            {timeline} projection
          </Badge>
        </div>
      </div>

      {/* BRI Improvement Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
        <CardContent className="pt-6 pb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/80">Projected Capability Improvement</p>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <p className="text-sm text-white/70">Current Avg BRI</p>
                  <p className="text-3xl font-bold">{briImprovement.current}</p>
                </div>
                <ArrowRight className="w-8 h-8 text-white/50" />
                <div>
                  <p className="text-sm text-white/70">Projected Avg BRI</p>
                  <p className="text-3xl font-bold">{briImprovement.projected}</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold">+{briImprovement.improvementPercentage}%</div>
              <p className="text-sm text-white/80">average BRI increase</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current vs Target State */}
      <div className="grid grid-cols-2 gap-6">
        {/* Current State */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-400"></div>
              Current State
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {segments.map((segment) => {
                const count = currentState[segment] || 0;
                const total = Object.values(currentState).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round(count / total * 100) : 0;
                const colors = segmentColors[segment];
                
                return (
                  <div key={segment}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium" style={{ color: colors.text }}>{segment}</span>
                      <span className="text-sm font-bold">{count} ({percentage}%)</span>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-3"
                      style={{ '--progress-color': colors.bg }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t text-center">
              <span className="text-sm text-muted-foreground">Total: </span>
              <span className="font-bold">{Object.values(currentState).reduce((a, b) => a + b, 0)} employees</span>
            </div>
          </CardContent>
        </Card>

        {/* Target State */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              Target State ({timeline})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {segments.map((segment) => {
                const count = targetState[segment] || 0;
                const total = Object.values(targetState).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round(count / total * 100) : 0;
                const colors = segmentColors[segment];
                
                return (
                  <div key={segment}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium" style={{ color: colors.text }}>{segment}</span>
                      <span className="text-sm font-bold">{count} ({percentage}%)</span>
                    </div>
                    <Progress 
                      value={percentage} 
                      className="h-3"
                      style={{ '--progress-color': colors.bg }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t text-center">
              <span className="text-sm text-muted-foreground">Total: </span>
              <span className="font-bold">{Object.values(targetState).reduce((a, b) => a + b, 0)} employees</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change Summary */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#C4A265]" />
            Projected Changes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {segments.map((segment) => {
              const change = changes[segment] || 0;
              const colors = segmentColors[segment];
              const isPositive = change > 0;
              const isNegative = change < 0;
              
              return (
                <Card key={segment} className="border" style={{ borderColor: colors.bg + '40' }}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge style={{ backgroundColor: colors.light, color: colors.text }}>
                        {segment}
                      </Badge>
                      {isPositive && <ChevronUp className="w-5 h-5 text-emerald-500" />}
                      {isNegative && <ChevronDown className="w-5 h-5 text-amber-500" />}
                      {!isPositive && !isNegative && <Minus className="w-5 h-5 text-slate-400" />}
                    </div>
                    <p className="text-2xl font-bold" style={{ color: isPositive ? '#10b981' : isNegative ? '#f59e0b' : '#64748b' }}>
                      {isPositive ? '+' : ''}{change}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isPositive ? 'through development' : isNegative ? 'moved to higher level' : 'no change'}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card className="border-0 shadow-sm bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-800">Key Transformation Goals</p>
              <ul className="mt-2 space-y-1 text-sm text-blue-700">
                <li>• Move {Math.abs(changes["Developing"] || 0)} Developing employees to Accelerating through targeted training</li>
                <li>• Reduce Upskilling Priority segment by {Math.abs(changes["Upskilling Priority"] || 0)} through intensive programs</li>
                <li>• Grow Builder segment by {changes["Builder"] || 0} through leadership development</li>
                <li>• Achieve overall BRI improvement of +{briImprovement.improvement} points</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
